import { useCallback, useState } from 'react';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import defaultTo from 'lodash/defaultTo';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import toLower from 'lodash/toLower';
import sortBy from 'lodash/sortBy';
import reverse from 'lodash/reverse';
import compact from 'lodash/compact';
import find from 'lodash/find';
import toString from 'lodash/toString';
import moment from 'moment';

export const Filter = {
  ANY_STATUS: 'any-status',
  ONLINE: 'online',
  OFFLINE: 'offline',
  IS_ACTIVATE: 'isActivate',
  ANY_OS: 'any-os',
  HUMMINGBOARD: 'hummingboard',
  WINDOWS: 'windows',
  SSSP: 'sssp',
  ANDROID: 'android',
  TIZEN: 'tizen',
  WEBOS: 'webos',
};

export const Sort = { NOVELTY: 'novelty', ASC: 'asc', DESC: 'desc' };

const FilterFn = {
  [Filter.ONLINE]: ({ online }) => online,
  [Filter.OFFLINE]: ({ online, isActivate }) => !online && isActivate,
  [Filter.IS_ACTIVATE]: ({ isActivate }) => !isActivate,
  [Filter.HUMMINGBOARD]: ({ platform }) => platform === Filter.HUMMINGBOARD,
  [Filter.WINDOWS]: ({ platform }) => platform === Filter.WINDOWS,
  [Filter.SSSP]: ({ platform }) => platform === Filter.SSSP,
  [Filter.ANDROID]: ({ platform }) => platform === Filter.ANDROID,
  [Filter.TIZEN]: ({ platform }) => platform === Filter.TIZEN,
  [Filter.WEBOS]: ({ platform }) => platform === Filter.WEBOS,
};

const statusFilters = [Filter.ONLINE, Filter.OFFLINE, Filter.IS_ACTIVATE];
const osFilters = [
  Filter.HUMMINGBOARD,
  Filter.WINDOWS,
  Filter.SSSP,
  Filter.ANDROID,
  Filter.TIZEN,
  Filter.WEBOS,
];

const useDevicesFilter = () => {
  const [filters, setFilters] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [sort, setSort] = useState(Sort.NOVELTY);

  const filterFns = compact(
    filter(
      map(filters, (filterName) => FilterFn[filterName]),
      (filterName) =>
        filterName !== Filter.ANY_OS && filterName !== Filter.ANY_STATUS
    )
  );

  const arrayFilter = (array, excluded = []) =>
    filter(array, (item) => !includes(excluded, item));

  const onSort = useCallback((value) => {
    setSort(value);
  }, []);
  const onSearch = useCallback((e) => {
    const value = e.target.value;

    setSearchValue(value);
  }, []);
  const onFilter = (value) => {
    if (value === null) {
      return setFilters([]);
    }

    let newFilters = defaultTo(filters, []).slice();
    let excluded = [];
    const anyStatus = value === Filter.ANY_STATUS;
    const anyOS = value === Filter.ANY_OS;

    if (anyStatus || anyOS) {
      excluded = anyStatus ? statusFilters : osFilters;

      if (includes(filters, value)) {
        excluded.push(value);
        return setFilters(arrayFilter(newFilters, excluded));
      } else {
        newFilters = arrayFilter(
          filters,
          anyStatus ? statusFilters : osFilters
        );
        newFilters.push(value);
        return setFilters(newFilters);
      }
    }

    if (includes(filters, value)) {
      setFilters(arrayFilter(newFilters, [value]));
    } else {
      newFilters.push(value);
      setFilters(newFilters);
    }
  };

  const filterDevices = (items = []) => {
    let newItems = [];

    const filterByName = (items) =>
      filter(items, ({ name }) =>
        includes(toLower(name), toLower(searchValue))
      );

    if (isEmpty(filterFns)) {
      return !isEmpty(searchValue) ? filterByName(items) : items;
    }

    map(filterFns, (filterFn) => {
      map(items, (item) => {
        if (
          filterFn &&
          filterFn(item) &&
          !find(newItems, ({ id }) => toString(id) === toString(item.id))
        ) {
          newItems.push(item);
        }
      });
    });

    if (!isEmpty(searchValue)) {
      newItems = filterByName(newItems);
    }

    return newItems;
  };

  const sortDevices = (items) => {
    if (sort === Sort.NOVELTY) {
      return items.sort((a, b) => {
        return moment(b.addTime) - moment(a.addTime);
      });
    } else if (sort === Sort.ASC) {
      return sortBy(items, ['name']);
    } else if (sort === Sort.DESC) {
      return reverse(sortBy(items, ['name']));
    }
  };

  return {
    filters,
    sort,
    filter: filterDevices,
    onFilter,
    onSearch,
    onSort,
    sortDevices,
  };
};

export default useDevicesFilter;
